import { useEffect, useMemo, useRef, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/style.css";
import { Box, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";

import {
  fromYmd,
  computeShipDate,
  toYmd,
  formatHuman,
  buildAllowedDateSet,
  lookupShipDate,
} from "./datesHelper";
import type { QivoraDate } from "@/api/qivoraClient";
import { QUIZ } from "@/theme/quizTokens";

type Props = {
  value?: string; // "YYYY-MM-DD"
  onChange?: (x: { deliveryDate: string; shipDate: string }) => void;
  qivoraDates?: QivoraDate[] | null;
  loading?: boolean;
  deliveryDayNames?: string[];
  cutoffHours?: number;
  /** Hide the confirmation rows (the parent shows the red invalid-selection strip instead). */
  suppressFooter?: boolean;
};

/** Format a list of day names for display: ["Tuesday", "Thursday"] → "Tuesdays & Thursdays" */
function formatDayNamesList(names: string[]): string {
  if (names.length === 0) return "";
  const pluralized = names.map((n) => n + "s");
  if (pluralized.length === 1) return pluralized[0];
  if (pluralized.length === 2) return `${pluralized[0]} & ${pluralized[1]}`;
  return pluralized.slice(0, -1).join(", ") + " & " + pluralized[pluralized.length - 1];
}

/** Circular tick used on the confirmed delivery/ship rows (Figma 16v2). */
function TickCircle({ color }: { color: string }) {
  return (
    <Box
      sx={{
        width: 26,
        height: 26,
        borderRadius: "50%",
        bgcolor: color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <CheckIcon sx={{ fontSize: 16, color: "#fff" }} />
    </Box>
  );
}

/**
 * Figma 16v2 calendar: white rounded panel, light-blue month header with arrows,
 * blue rounded-square selected day; confirmed dates render as a second white
 * panel with green/blue circular ticks.
 */
export function PawsDayPicker({ value, onChange, qivoraDates, loading, deliveryDayNames, cutoffHours, suppressFooter }: Props) {
  const [localSelected, setLocalSelected] = useState<Date | undefined>(() =>
    fromYmd(value)
  );

  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNowTick(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  void nowTick;

  // When Qivora dates are provided, use them for allowed/disabled logic
  const allowedSet = useMemo(
    () => (qivoraDates ? buildAllowedDateSet(qivoraDates) : null),
    [qivoraDates]
  );

  const isDisabled = (date: Date) => {
    if (allowedSet) {
      return !allowedSet.has(toYmd(date));
    }
    // No API data — disable all dates (don't fall back to hardcoded rules)
    return true;
  };

  const nextAvailable = useMemo(() => {
    if (qivoraDates?.length) {
      return fromYmd(qivoraDates[0].date);
    }
    return undefined;
  }, [qivoraDates]);

  // Sync local state with parent-controlled value
  useEffect(() => {
    const v = fromYmd(value);
    setLocalSelected(v);
  }, [value]);

  // Displayed month is CONTROLLED so the calendar always opens on (and jumps
  // to) the month of the selected or first-available date. With the previous
  // uncontrolled `defaultMonth`, mounting near month end while dates were
  // still loading left the calendar stuck on the current month even though
  // every available date was in the next one — users had to notice and page
  // forward themselves. The ref stops the snap re-firing while a user is
  // freely browsing months: we only jump when the target month actually
  // changes (initial load, auto-select, or a new selection).
  const [month, setMonth] = useState<Date | undefined>(() => localSelected ?? nextAvailable);
  const lastMonthSnapRef = useRef<string | null>(null);
  useEffect(() => {
    const target = localSelected ?? nextAvailable;
    if (!target) return;
    const key = `${target.getFullYear()}-${target.getMonth()}`;
    if (lastMonthSnapRef.current === key) return;
    lastMonthSnapRef.current = key;
    setMonth(new Date(target.getFullYear(), target.getMonth(), 1));
  }, [localSelected, nextAvailable]);

  const shipDate = useMemo(() => {
    if (!localSelected) return null;
    if (qivoraDates) {
      const apiShip = lookupShipDate(qivoraDates, toYmd(localSelected));
      if (apiShip) return fromYmd(apiShip) ?? computeShipDate(localSelected);
    }
    return computeShipDate(localSelected);
  }, [localSelected, qivoraDates]);

  const dayNamesDisplay = formatDayNamesList(deliveryDayNames ?? []);
  const cutoffDisplay = cutoffHours ?? 36;

  return (
    <Box>
      {/* Calendar panel */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "14px",
          overflow: "hidden",
          pb: "10px",
          position: "relative",

          "& .rdp": {
            "--rdp-accent-color": "var(--pop-blue, #8ee5ff)",
            "--rdp-accent-background-color": "var(--pop-blue, #8ee5ff)",
            "--rdp-day-height": "38px",
            "--rdp-day-width": "44px",
            fontFamily: "var(--pop-font-body), Archivo, sans-serif",
            margin: 0,
            width: "100%",
          },
          "& .rdp-months, & .rdp-month": {
            width: "100%",
            maxWidth: "100%",
          },
          // Light-blue month header band
          "& .rdp-month_caption": {
            background: "var(--pop-blue, #8ee5ff)",
            justifyContent: "center",
            padding: "13px 0",
            fontWeight: 700,
            color: "var(--pop-ink, #332115)",
            fontSize: "1rem",
          },
          "& .rdp-nav": {
            position: "absolute",
            top: "4px",
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "space-between",
            px: "10px",
            zIndex: 1,
          },
          "& .rdp-button_previous, & .rdp-button_next": {
            color: "var(--pop-ink, #332115)",
            borderRadius: "8px",
            "&:hover": { background: "rgba(255,255,255,0.35)" },
          },
          "& .rdp-chevron": {
            fill: "var(--pop-ink, #332115)",
          },
          "& .rdp-month_grid": {
            width: "calc(100% - 24px)",
            margin: "10px auto 0",
          },
          "& .rdp-weekday": {
            color: "#9098a3",
            fontWeight: 600,
            fontSize: "0.82rem",
          },
          "& .rdp-day": {
            borderRadius: "10px",
            transition: "all 0.15s ease",
            textAlign: "center",
          },
          "& .rdp-day:not([disabled]):hover": {
            background: "#e9f8ff",
          },
          "& .rdp-day_button": {
            fontWeight: 600,
            fontSize: "0.9rem",
            color: "var(--pop-ink, #332115)",
            margin: "0 auto",
          },
          "& .rdp-selected .rdp-day_button": {
            background: "var(--pop-blue, #8ee5ff)",
            border: "none",
            color: "var(--pop-ink, #332115)",
            fontWeight: 700,
            borderRadius: "10px",
          },
          "& .rdp-today .rdp-day_button": {
            border: "none",
            fontWeight: 600,
          },
          "& .rdp-disabled .rdp-day_button": {
            color: "#c8c2bb",
            textDecoration: "none",
            opacity: 1,
          },
          "& .rdp-outside .rdp-day_button": {
            color: "#d5d0c9",
          },
        }}
      >
        <DayPicker
          animate
          mode="single"
          selected={localSelected}
          month={month}
          onMonthChange={setMonth}
          onSelect={(date) => {
            setLocalSelected(date);
            if (!date) return;
            const ymd = toYmd(date);
            const apiShip = qivoraDates ? lookupShipDate(qivoraDates, ymd) : undefined;
            const ship = apiShip ?? toYmd(computeShipDate(date));
            onChange?.({ deliveryDate: ymd, shipDate: ship });
          }}
          disabled={isDisabled}
        />
      </Box>

      {/* Confirmed dates / helper panel */}
      {!suppressFooter && (
        <Box sx={{ mt: "12px" }}>
          {localSelected ? (
            <Box
              sx={{
                background: "#fff",
                borderRadius: "14px",
                p: "16px 18px",
                display: "flex",
                flexDirection: "column",
                gap: "14px",
              }}
            >
              {/* Selected delivery date */}
              <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <TickCircle color="#7ec24a" />
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      color: "#9098a3",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.6px",
                      fontFamily: QUIZ.font.body,
                    }}
                  >
                    Delivery Date
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "1.05rem",
                      color: QUIZ.color.ink,
                      fontWeight: 700,
                      fontFamily: QUIZ.font.body,
                    }}
                  >
                    {formatHuman(localSelected)}
                  </Typography>
                </Box>
              </Box>

              {/* "Ships on" intentionally not shown — the arrival date is all the user needs.
                  (shipDate is still tracked in state for checkout/Qivora.) */}
            </Box>
          ) : (
            <Box
              sx={{
                textAlign: "center",
                background: "#fff",
                borderRadius: "14px",
                p: "16px 18px",
              }}
            >
              {dayNamesDisplay ? (
                <Typography sx={{ fontSize: "0.9rem", color: QUIZ.color.ink, mb: 1, fontFamily: QUIZ.font.body }}>
                  Delivery days are <strong>{dayNamesDisplay}</strong>
                </Typography>
              ) : (
                <Typography sx={{ fontSize: "0.9rem", color: QUIZ.color.ink, mb: 1, fontFamily: QUIZ.font.body }}>
                  {loading ? "Loading delivery dates..." : "Select a delivery date"}
                </Typography>
              )}
              <Typography sx={{ fontSize: "0.8rem", color: "#9098a3", fontFamily: QUIZ.font.body }}>
                Must be booked at least <strong>{cutoffDisplay} hours</strong> in advance
              </Typography>
              {nextAvailable && (
                <Typography
                  sx={{
                    mt: 1,
                    fontSize: "0.85rem",
                    color: QUIZ.color.brown,
                    fontWeight: 600,
                    fontFamily: QUIZ.font.body,
                  }}
                >
                  Next available: {formatHuman(nextAvailable)}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
